import find from 'lodash/find'
import { get, isEmpty } from 'lodash'
import { default as deserialize, formatMeta } from 'app/utils/deserialize'
import { fetch } from 'app/utils/data'
import { getCanonicalFamilyStyle } from 'app/views/icons/iconAccess'

const INITIAL_STATE = Object.freeze({
  list: [],
  item: null,
  subscriptionDiscounts: [],
  affiliateMeta: null
})

const iconIsIncludedInPlan = (state) => (icon, planSku) => {
  if (icon.is_free) return true

  const { family, style } = getCanonicalFamilyStyle(icon)
  const plan = find(state.list, { sku: planSku })

  if (!plan?.iconCollection?.familyStyles?.length) return false

  const isPaidPlan = plan.sku !== 'FA-FREE'
  const iconExists = plan.iconCollection.familyStyles.some((fs) => {
    return fs.family === family && fs.style === style
  })

  return isPaidPlan && iconExists
}

const getPlansIncluding = (state) => (icon, action, subscription, isCustomIcon, requireProPlus) => {
  if (isEmpty(icon)) {
    if (requireProPlus) {
      return state.list.filter(
        (plan) =>
          plan.iconCollection.name === 'pro-plus-collection' &&
          plan.active &&
          plan.sku !== 'FA-FREE' &&
          plan.sku.startsWith('FA') &&
          plan.price.amount > 0 &&
          !!plan[action] &&
          plan.sku.toLowerCase().includes('plus')
      )
    } else {
      return state.list.filter((plan) => plan.active && plan.sku !== 'FA-FREE' && plan.sku.startsWith('FA') && plan.price.amount > 0 && !!plan[action])
    }
  }

  if (isCustomIcon) {
    const userCurrentPlanPrice = subscription?.plan?.price?.amount || 0

    return state.list.filter(
      (plan) =>
        plan.active &&
        plan.sku !== 'FA-FREE' &&
        plan.sku.startsWith('FA') &&
        plan.price.amount > 0 &&
        plan.price.amount > userCurrentPlanPrice &&
        !!plan[action]
    )
  }

  const { family, style } = getCanonicalFamilyStyle(icon)
  const iconCheck = iconIsIncludedInPlan(state)

  return state.list.filter((plan) => {
    return iconCheck(icon, plan.sku) && plan?.iconCollection?.familyStyles.some((fs) => fs.family === family && fs.style === style) && !!plan[action]
  })
}

const getPlansForUpgrade =
  (state) =>
  (options = {}) => {
    const { icon, userAction, subscription, isCustomIcon, requireProPlus = false } = options
    const plans = getPlansIncluding(state)(icon, userAction, subscription, isCustomIcon, requireProPlus) || []

    return Array.isArray(plans) ? plans : []
  }

const getNextUpgradePlans =
  (_state) =>
  (plans = [], subscription) => {
    if (!Array.isArray(plans)) return []

    const currentUserPlanAmount = subscription?.plan?.price?.amount || 0
    const currentPlanSku = subscription?.plan?.sku

    const result = plans
      .filter((plan) => {
        const isCurrentPlanActive = !subscription?.endedAt && plan?.sku === currentPlanSku
        const isPlusPlan = plan?.sku?.toLowerCase().includes('plus')
        const isCurrentPlanPlus = currentPlanSku?.toLowerCase().includes('plus')

        // Allow upgrading to Plus plans only if current plan is not a Plus plan
        // or if upgrading to a higher tier Plus plan
        const canUpgradeToPlus = !isCurrentPlanPlus || (isPlusPlan && plan.price.amount > currentUserPlanAmount)

        return (
          plan?.price?.amount >= currentUserPlanAmount &&
          plan?.sku !== 'FA-FREE' &&
          plan.sku.startsWith('FA') &&
          canUpgradeToPlus &&
          !isCurrentPlanActive &&
          plan.active &&
          plan.price.amount > 0
        )
      })
      .sort((a, b) => a.price?.amount - b.price?.amount)
      .map((plan) => plan.sku)
      .slice(0, 3)

    return result
  }

const skusForFilteredPlans =
  (state) =>
  (options = {}) => {
    const plans = getPlansForUpgrade(state)(options)
    const upgradePlans = getNextUpgradePlans(state)(plans, options.subscription)

    return upgradePlans
  }

export default {
  namespaced: true,

  state: { ...INITIAL_STATE },

  getters: {
    iconIsIncludedInPlan,
    getPlansIncluding,
    getPlansForUpgrade,
    getNextUpgradePlans,
    skusForFilteredPlans,

    currentPlans: (state) => state.list.filter((plan) => plan.active),

    allPlans: (state) => state.list,

    currentPlan: (state) => state.item,

    getPlan: (state) => (sku) => {
      return find(state.list, { sku: sku })
    },

    getPlanBySlug: (state) => (slug) => {
      return find(state.list, { slug: slug })
    },

    affiliateMeta: (state) => state.affiliateMeta,

    discount: (state) => state.discount,

    subscriptionDiscounts: (state) => state.subscriptionDiscounts
  },

  mutations: {
    plansFetched(state, { plans, affiliateMeta }) {
      state.list = plans
      state.affiliateMeta = affiliateMeta
    },

    planFetched(state, { plan }) {
      state.item = plan
    },

    discountsFetched(state, { discounts }) {
      state.subscriptionDiscounts = discounts
    },

    resetState(state) {
      state = { ...INITIAL_STATE }
    }
  },

  actions: {
    async fetchPlans({ commit }) {
      const response = await fetch({ url: `/api/plans` })

      const plans = await deserialize(response.data)
      const affiliateMeta = formatMeta(get(response.data, ['meta', 'affiliate']))

      commit('plansFetched', { plans, affiliateMeta })

      return plans
    },

    async fetchPlan({ commit }, sku) {
      const response = await fetch({ url: `/api/plans/${sku}` })

      const plan = await deserialize(response.data)

      commit('planFetched', { plan })

      return plan
    },

    async fetchDiscounts({ commit }, couponIds) {
      try {
        const response = await fetch({ url: `/api/discounts`, params: { couponIds: couponIds } })

        const discounts = await deserialize(response.data)

        commit('discountsFetched', { discounts: discounts })

        return discounts
      } catch (error) {
        return error
      }
    }
  }
}
